import React, { useEffect, useMemo, useState } from "react";
import {
  useCampaigns,
  useCreateCampaign,
  useDeleteCampaign,
  useUpdateCampaign,
} from "../../hooks/useCampaigns";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Select, Textarea } from "../../components/ui/Input";
import { useClients } from "../../hooks/useClients";
import { CampaignCreate, CampaignUpdate } from "../../types/campaign";
import { useNavigate } from "react-router";

export default function CampaignsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [clientFilter, setClientFilter] = useState<number | "">("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CampaignCreate>({
    client_id: 0,
    name: "",
    description: "",
    program_id: undefined,
    bot_name: "",
    color_palette: undefined,
    avatar_url: "",
    max_auth_attempts: 3,
    session_timeout_seconds: 300,
  });

  const { data, isLoading, isError, refetch } = useCampaigns({
    page,
    page_size: 12,
    search: search || undefined,
    client_id: clientFilter || undefined,
  });
  const { data: clientsData } = useClients({ page_size: 100 });
  const clients = clientsData?.clients || [];
  const navigate = useNavigate();

  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();

  const totalPages = data?.total_pages || 1;
  const campaigns = data?.campaigns || [];

  useEffect(() => {
    if (!formData.client_id && clients.length > 0) {
      setFormData((prev) => ({ ...prev, client_id: clients[0].id }));
    }
  }, [clients]);

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      client_id: clients[0]?.id || 0,
      name: "",
      description: "",
      program_id: undefined,
      bot_name: "",
      color_palette: undefined,
      avatar_url: "",
      max_auth_attempts: 3,
      session_timeout_seconds: 300,
    });
    setShowForm(true);
  };

  const openEdit = (c: any) => {
    navigate(`/campaigns/${c.id}/edit`);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.client_id) return;
    const payload: CampaignCreate | CampaignUpdate = editingId
      ? {
          client_id: formData.client_id,
          name: formData.name || undefined,
          description: formData.description || undefined,
          program_id: formData.program_id ?? undefined,
          bot_name: formData.bot_name || undefined,
          color_palette: formData.color_palette ?? undefined,
          avatar_url: formData.avatar_url || undefined,
          max_auth_attempts: formData.max_auth_attempts ?? undefined,
          session_timeout_seconds:
            formData.session_timeout_seconds ?? undefined,
        }
      : {
          client_id: formData.client_id,
          name: formData.name.trim(),
          description: formData.description || undefined,
          program_id: formData.program_id ?? undefined,
          bot_name: formData.bot_name || undefined,
          color_palette: formData.color_palette ?? undefined,
          avatar_url: formData.avatar_url || undefined,
          max_auth_attempts: formData.max_auth_attempts ?? undefined,
          session_timeout_seconds:
            formData.session_timeout_seconds ?? undefined,
        };

    if (editingId) {
      await updateMutation.mutateAsync({
        id: editingId,
        data: payload as CampaignUpdate,
      });
    } else {
      await createMutation.mutateAsync(payload as CampaignCreate);
    }
    setShowForm(false);
    refetch();
  };

  const onDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-600 mt-1">Manage campaigns per client</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Create Campaign
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 flex gap-3">
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or description"
            variant="default"
            className="flex-1"
            showLabel={false}
          />
          <Select
            variant="default"
            value={clientFilter}
            onChange={(e) =>
              setClientFilter(e.target.value ? Number(e.target.value) : "")
            }
            showLabel={false}
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(1)}
            className="px-3 py-2 bg-gray-100 rounded border"
          >
            Apply
          </button>
          <button
            onClick={() => {
              setSearch("");
              setClientFilter("");
              setPage(1);
            }}
            className="px-3 py-2 bg-gray-100 rounded border"
          >
            Clear
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {isError && (
        <ErrorMessage
          message="Failed to load campaigns"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((c) => (
            <div key={c.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{c.name}</h3>
                <span className="text-xs text-gray-500">#{c.id}</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Client:{" "}
                <span className="font-mono">
                  {clients.find((cl) => cl.id === c.client_id)?.name ||
                    c.client_id}
                </span>
              </div>
              {c.description && (
                <div className="text-sm text-gray-700 mb-3">
                  {c.description}
                </div>
              )}
              <div className="text-xs text-gray-500 mb-3">
                Auth attempts: {c.max_auth_attempts ?? 3}, Timeout:{" "}
                {c.session_timeout_seconds ?? 300}s
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => openEdit(c)}
                  className="px-3 py-1 text-sm bg-white border rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(c.id)}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && (data?.total_pages || 0) > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? "Edit Campaign" : "Create Campaign"}
              </h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client
                    </label>
                    <select
                      value={formData.client_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          client_id: Number(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                      required
                    >
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Textarea
                    label="Description"
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    variant="rounded-md"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program ID
                    </label>
                    <input
                      type="number"
                      value={formData.program_id ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          program_id: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bot Name
                    </label>
                    <input
                      type="text"
                      value={formData.bot_name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, bot_name: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Avatar URL
                    </label>
                    <input
                      type="text"
                      value={formData.avatar_url || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, avatar_url: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Auth Attempts
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.max_auth_attempts ?? 3}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_auth_attempts: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.session_timeout_seconds ?? 300}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        session_timeout_seconds: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <Textarea
                    label="Color Palette (JSON)"
                    value={JSON.stringify(
                      formData.color_palette ?? {},
                      null,
                      2,
                    )}
                    onChange={(e) => {
                      try {
                        const parsed = e.target.value
                          ? JSON.parse(e.target.value)
                          : undefined;
                        setFormData({ ...formData, color_palette: parsed });
                      } catch (_) {}
                    }}
                    variant="rounded-md"
                    className="font-mono"
                    rows={6}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    {editingId ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
